import {Grid} from '@chakra-ui/react';
import {DriveFile, isFolder, isImageFile} from '../drive/types';
import {PlazmaStars} from '../hooks/usePlazmaStars';
import {isCoverFile} from '../domain/eventInfo';
import {useInfoFileIndex} from '../drive/queries';
import {FileCard} from './FileCard';
import {FolderCard} from './FolderCard';

export const GRID_COLUMNS = {base: 2, sm: 3, md: 4, xl: 6};

// Up to the xl breakpoint, retain the established fixed-column layout.  Beyond
// that point, each track starts at 300px and may grow to, until an extra tile fits.
const GRID_TEMPLATE_COLUMNS = {
    base: 'repeat(2, minmax(0, 1fr))',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    xl: 'repeat(auto-fill, minmax(300px, 1fr))',
};

interface EntryGridProps {
    entries: DriveFile[];
    driveId: string;
    selected: Set<string>;
    onSelectEntry: (entry: DriveFile, e: React.MouseEvent) => void;
    onOpenFile: (file: DriveFile) => void;
    onOpenFolder: (folder: DriveFile) => void;
    /** Right-click on an entry (the grid's own context menu replaces the browser's). */
    onEntryContextMenu?: (entry: DriveFile, e: React.MouseEvent) => void;
    /** Set in the web bin: image cards get a star that copies them to plazma. */
    plazma?: PlazmaStars;
    /** The album's info.json `cover` value; the matching image card is outlined. */
    cover?: string;
}

/** Renders a mixed grid of subfolders and files, dispatching to FolderCard/FileCard per entry. */
export function EntryGrid({
    entries,
    driveId,
    selected,
    onSelectEntry,
    onOpenFile,
    onOpenFolder,
    onEntryContextMenu,
    plazma,
    cover,
}: EntryGridProps) {
    // One drive-wide info.json search serves every folder card (titles of event folders, album covers) — skipped without folders.
    const hasFolders = entries.some(isFolder);
    const infoIndex = useInfoFileIndex(driveId, hasFolders).data;

    return <Grid templateColumns={GRID_TEMPLATE_COLUMNS} gap="12px">
        {entries.map((entry) =>
            isFolder(entry)
                ? <FolderCard
                    key={entry.id}
                    folder={entry}
                    driveId={driveId}
                    infoFile={infoIndex?.get(entry.id)}
                    selected={selected.has(entry.id)}
                    onSelect={(e) => onSelectEntry(entry, e)}
                    onOpen={() => onOpenFolder(entry)}
                    onContextMenu={onEntryContextMenu && ((e) => onEntryContextMenu(entry, e))}
                />
                : <FileCard
                    key={entry.id}
                    file={entry}
                    selected={selected.has(entry.id)}
                    onSelect={(e) => onSelectEntry(entry, e)}
                    onOpen={() => onOpenFile(entry)}
                    onContextMenu={onEntryContextMenu && ((e) => onEntryContextMenu(entry, e))}
                    isCover={isCoverFile(cover, entry.name)}
                    star={plazma?.isReady && isImageFile(entry) ? {
                        starred: plazma.isStarred(entry),
                        isPending: plazma.isPending(entry),
                        isDisabled: !plazma.canToggle(entry),
                        onToggle: () => plazma.toggle(entry),
                    } : undefined}
                />
        )}
    </Grid>;
}
